import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import { CalendarDays, Filter, X, Phone, Mail, MapPin, Users, MessageSquare } from "lucide-react";

import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import AssignDriverModal from "./AssignDriverModal";

const STATUS_OPTIONS = [
  { v: "all", l: "All statuses" },
  { v: "pending", l: "Pending" },
  { v: "confirmed", l: "Confirmed" },
  { v: "needs_driver", l: "Confirmed - needs driver" },
  { v: "awaiting_payment", l: "Awaiting payment" },
  { v: "completed", l: "Completed" },
  { v: "cancelled", l: "Cancelled" },
  { v: "proposal_sent", l: "Proposal sent" },
  { v: "proposal_accepted", l: "Proposal accepted" },
];

// Drivers only see statuses that can actually be assigned to them
const DRIVER_STATUS_OPTIONS = [
  { v: "all", l: "All" },
  { v: "confirmed", l: "Confirmed (upcoming)" },
  { v: "completed", l: "Completed" },
  { v: "cancelled", l: "Cancelled" },
];

export default function CalendarTab({ scope = "admin" }) {
  const calendarRef = useRef(null);
  const [drivers, setDrivers] = useState([]);
  const [filters, setFilters] = useState({ status: "all", driver_id: "all", origin: "", destination: "" });
  const [selected, setSelected] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const isAdmin = scope === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/admin/drivers`)
      .then((res) => setDrivers(res.data || []))
      .catch(() => setDrivers([]));
  }, [isAdmin]);

  const fetchEvents = useMemo(() => async (info, success, failure) => {
    const url = isAdmin ? `${API}/admin/calendar` : `${API}/driver/calendar`;
    const params = { start: info.startStr, end: info.endStr };
    if (isAdmin) {
      if (filters.status !== "all") params.status = filters.status;
      if (filters.driver_id !== "all") params.driver_id = filters.driver_id;
      if (filters.origin) params.origin = filters.origin;
      if (filters.destination) params.destination = filters.destination;
    } else {
      if (filters.status !== "all") params.status = filters.status;
    }
    try {
      const res = await axios.get(url, { params });
      success(res.data || []);
    } catch (err) {
      failure(err);
      toast.error(err.response?.data?.detail || "Failed to load calendar");
    }
  }, [isAdmin, filters]);

  const refetch = () => {
    if (calendarRef.current) calendarRef.current.getApi().refetchEvents();
    setRefreshKey((k) => k + 1);
  };

  const onAssignSuccess = () => {
    setAssignModal(null);
    setSelected(null);
    refetch();
    toast.success("Driver assigned");
  };

  return (
    <div data-testid="calendar-tab" className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[#C5A059]" />
            {isAdmin ? "Calendar" : "My Transfers Calendar"}
          </h2>
          <p className="text-white/40 text-xs mt-1">
            {isAdmin ? "Day / week / month views with filters; click any event for details." : "Your assigned confirmed transfers."}
          </p>
        </div>
      </div>

      {isAdmin && (
        <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4 flex flex-wrap gap-3 items-end" data-testid="calendar-filters">
          <div className="flex-1 min-w-[180px]">
            <label className="text-white/50 text-[10px] uppercase mb-1 block flex items-center gap-1"><Filter className="w-3 h-3" /> Status</label>
            <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
              <SelectTrigger className="input-dark"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1F1F1F] border-[#333]">
                {STATUS_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v} className="text-white hover:bg-[#333]">{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="text-white/50 text-[10px] uppercase mb-1 block">Driver</label>
            <Select value={filters.driver_id} onValueChange={(v) => setFilters((f) => ({ ...f, driver_id: v }))}>
              <SelectTrigger className="input-dark"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1F1F1F] border-[#333]">
                <SelectItem value="all" className="text-white hover:bg-[#333]">All drivers</SelectItem>
                <SelectItem value="none" className="text-white hover:bg-[#333]">Unassigned</SelectItem>
                {drivers.map((d) => (
                  <SelectItem key={d.driver_id} value={d.driver_id} className="text-white hover:bg-[#333]">{d.name} {d.surname}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-white/50 text-[10px] uppercase mb-1 block">Origin contains</label>
            <Input value={filters.origin} onChange={(e) => setFilters((f) => ({ ...f, origin: e.target.value }))} placeholder="e.g. Venice" className="input-dark" data-testid="calendar-filter-origin" />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-white/50 text-[10px] uppercase mb-1 block">Destination contains</label>
            <Input value={filters.destination} onChange={(e) => setFilters((f) => ({ ...f, destination: e.target.value }))} placeholder="e.g. Cortina" className="input-dark" data-testid="calendar-filter-destination" />
          </div>
          <Button variant="ghost" onClick={refetch} className="text-white/60" data-testid="calendar-apply-filters">Apply</Button>
        </div>
      )}

      {!isAdmin && (
        <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4 flex flex-wrap gap-3 items-end" data-testid="driver-calendar-filters">
          <div className="flex-1 min-w-[200px] max-w-sm">
            <label className="text-white/50 text-[10px] uppercase mb-1 block flex items-center gap-1"><Filter className="w-3 h-3" /> Status</label>
            <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
              <SelectTrigger className="input-dark" data-testid="driver-calendar-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1F1F1F] border-[#333]">
                {DRIVER_STATUS_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v} className="text-white hover:bg-[#333]">{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="ghost" onClick={refetch} className="text-white/60" data-testid="driver-calendar-apply">Refresh</Button>
        </div>
      )}

      <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-4" data-testid="calendar-container">
        <FullCalendar
          key={refreshKey + ":" + JSON.stringify(filters) + ":" + scope}
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
          initialView={isAdmin ? "dayGridMonth" : "listWeek"}
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
          }}
          height="auto"
          firstDay={1}
          nowIndicator
          eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
          slotLabelFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
          events={fetchEvents}
          eventClick={(info) => {
            info.jsEvent.preventDefault();
            setSelected(info.event.extendedProps);
          }}
          eventDidMount={(info) => {
            if (info.event.extendedProps.needs_driver) {
              info.el.style.boxShadow = "inset 0 0 0 2px #EF4444";
            }
          }}
        />
      </div>

      {selected && (
        <CalendarEventDetails
          event={selected}
          isAdmin={isAdmin}
          onClose={() => setSelected(null)}
          onAssign={(qid) => setAssignModal(selected)}
        />
      )}

      {assignModal && (
        <AssignDriverModal
          quote={assignModal}
          onClose={() => setAssignModal(null)}
          onAssigned={onAssignSuccess}
        />
      )}
    </div>
  );
}

function CalendarEventDetails({ event, isAdmin, onClose, onAssign }) {
  const phoneClean = (event.user_phone || "").replace(/[^0-9+]/g, "");
  const waLink = phoneClean ? `https://wa.me/${phoneClean.replace(/^\+/, "")}` : null;
  const telLink = phoneClean ? `tel:${phoneClean}` : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="calendar-event-modal">
      <div className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h3 className="text-lg font-semibold text-white">Booking {event.quote_id}</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="calendar-event-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4 text-sm text-white/80">
          <div className="flex items-center gap-2 text-[#C5A059]">
            <MapPin className="w-4 h-4" />
            <span className="font-medium">{event.origin}</span>
            <span className="text-white/40">→</span>
            <span className="font-medium">{event.destination}</span>
          </div>
          <div className="grid grid-cols-2 gap-3 bg-[#0A0A0A] border border-white/5 rounded-sm p-3 text-xs">
            <div><div className="text-white/40 uppercase mb-0.5">Pickup</div>{event.pickup_date} {event.pickup_time}</div>
            <div><div className="text-white/40 uppercase mb-0.5">Status</div><span className="capitalize text-white">{(event.status || "").replace("_", " ")}</span></div>
            <div><div className="text-white/40 uppercase mb-0.5">Customer</div>{event.user_name || "-"}</div>
            <div><div className="text-white/40 uppercase mb-0.5">Passengers</div><Users className="w-3 h-3 inline-block mr-1" />{event.passengers ?? "-"}</div>
            {event.is_round_trip && (
              <div className="col-span-2"><div className="text-white/40 uppercase mb-0.5">Return</div>{event.return_date} {event.return_time}</div>
            )}
            <div className="col-span-2"><div className="text-white/40 uppercase mb-0.5">Driver</div>{event.driver_name || <span className="text-red-400">Unassigned</span>}</div>
            {event.total_price != null && (
              <div className="col-span-2"><div className="text-white/40 uppercase mb-0.5">Total</div><span className="text-[#C5A059] font-mono">€{event.total_price}</span></div>
            )}
            {event.notes && (
              <div className="col-span-2"><div className="text-white/40 uppercase mb-0.5">Notes</div><span className="whitespace-pre-wrap text-white/60">{event.notes}</span></div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {telLink && (
              <a href={telLink} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs px-3 py-2 rounded-sm flex items-center gap-1" data-testid="calendar-event-call">
                <Phone className="w-3.5 h-3.5" /> Call {event.user_phone}
              </a>
            )}
            {waLink && (
              <a href={waLink} target="_blank" rel="noreferrer" className="bg-[#25D366] hover:bg-[#20bd5a] text-white text-xs px-3 py-2 rounded-sm flex items-center gap-1" data-testid="calendar-event-whatsapp">
                <MessageSquare className="w-3.5 h-3.5" /> WhatsApp
              </a>
            )}
            {event.user_email && (
              <a href={`mailto:${event.user_email}`} className="bg-white/5 hover:bg-white/10 text-white/80 text-xs px-3 py-2 rounded-sm flex items-center gap-1" data-testid="calendar-event-email">
                <Mail className="w-3.5 h-3.5" /> {event.user_email}
              </a>
            )}
            {isAdmin && event.needs_driver && (
              <Button onClick={() => onAssign(event.quote_id)} size="sm" className="btn-gold ml-auto" data-testid="calendar-event-assign-driver">
                Assign driver
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
